import {
  configApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import useAsync from 'react-use/lib/useAsync';

/**
 * Whether the current user may see the Certificate Inventory (sidebar item and
 * page).
 *
 * Controlled by `certInventory.allowedGroups`: when set, only users who belong
 * to one of those catalog groups can see it; when empty or unset, everyone can.
 * This lets an initial rollout be limited to specific teams.
 */
export function useCertInventoryVisible(): {
  loading: boolean;
  visible: boolean;
} {
  const configApi = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const allowedGroups =
    configApi.getOptionalStringArray('certInventory.allowedGroups') ?? [];

  const { loading, value } = useAsync(async () => {
    if (allowedGroups.length === 0) {
      return true;
    }
    const { ownershipEntityRefs } = await identityApi.getBackstageIdentity();
    const allowed = new Set(allowedGroups.map(ref => ref.toLowerCase()));
    return ownershipEntityRefs.some(ref => allowed.has(ref.toLowerCase()));
  }, [allowedGroups.join(','), identityApi]);

  return { loading, visible: value ?? false };
}
